import { diffLines, type ChangeObject } from 'diff';
import { cn } from '@/lib/utils';

type Side = { text: string; tone: 'same' | 'removed' | 'added' | 'blank' };
type Row = { left: Side; right: Side } | { gap: number };

const CONTEXT = 3;
const BLANK: Side = { text: '', tone: 'blank' };

const linesOf = (part: ChangeObject<string>) =>
  part.value.replace(/\n$/, '').split('\n');

/** Unchanged runs longer than the context fold into one "N lines" row. */
const sameRows = (
  lines: string[],
  isFirst: boolean,
  isLast: boolean
): Row[] => {
  const keepHead = isFirst ? 0 : CONTEXT;
  const keepTail = isLast ? 0 : CONTEXT;
  const row = (text: string): Row => ({
    left: { text, tone: 'same' },
    right: { text, tone: 'same' },
  });
  if (lines.length <= keepHead + keepTail + 1) return lines.map(row);
  return [
    ...lines.slice(0, keepHead).map(row),
    { gap: lines.length - keepHead - keepTail },
    ...lines.slice(lines.length - keepTail).map(row),
  ];
};

/** A removal followed by an addition lines up side by side, row for row. */
const changedRows = (removed: string[], added: string[]): Row[] =>
  Array.from({ length: Math.max(removed.length, added.length) }, (_, i) => ({
    left: i < removed.length ? { text: removed[i], tone: 'removed' } : BLANK,
    right: i < added.length ? { text: added[i], tone: 'added' } : BLANK,
  }));

/** Rows never reorder, so their position is a stable identity. */
const withIds = (rows: Row[]) =>
  rows.map((row, position) => ({ row, id: `row-${position}` }));

const linesIf = (part: ChangeObject<string>, keep: boolean) =>
  keep ? linesOf(part) : [];

const isUnchanged = (part: ChangeObject<string>) =>
  !part.added && !part.removed;

const isEditPair = (parts: ChangeObject<string>[], i: number) =>
  Boolean(parts[i].removed && parts[i + 1]?.added);

const oneSided = (part: ChangeObject<string>) =>
  changedRows(
    linesIf(part, Boolean(part.removed)),
    linesIf(part, Boolean(part.added))
  );

/** One part, or a removal and the addition right after it; returns parts used. */
const rowsAt = (parts: ChangeObject<string>[], i: number): [Row[], number] => {
  if (isUnchanged(parts[i])) {
    return [sameRows(linesOf(parts[i]), i === 0, i === parts.length - 1), 1];
  }
  if (isEditPair(parts, i)) {
    return [changedRows(linesOf(parts[i]), linesOf(parts[i + 1])), 2];
  }
  return [oneSided(parts[i]), 1];
};

const buildRows = (parts: ChangeObject<string>[]) => {
  const rows: Row[] = [];
  for (let i = 0; i < parts.length; ) {
    const [more, used] = rowsAt(parts, i);
    rows.push(...more);
    i += used;
  }
  return rows;
};

const TONE: Record<Side['tone'], string> = {
  same: '',
  removed: 'bg-red-100 text-red-900',
  added: 'bg-green-100 text-green-900',
  blank: 'bg-neutral-50',
};

function Cell({ side }: { side: Side }) {
  return (
    <td
      className={cn(
        'whitespace-pre-wrap break-all px-2 py-0.5 align-top',
        TONE[side.tone]
      )}
    >
      {side.text}
    </td>
  );
}

/**
 * Before on the left, after on the right. `timeout` bounds the diff on large
 * G-code; past it the caller shows a summary instead.
 */
export default function TextSplitDiff({
  before,
  after,
  mono = false,
}: {
  before: string;
  after: string;
  mono?: boolean;
}) {
  const parts = diffLines(before, after, { timeout: 4000 });
  if (!parts) {
    return (
      <p className="text-sm text-muted-foreground">
        Too many differences to show line by line.
      </p>
    );
  }
  return (
    <div className="max-h-[28rem] overflow-auto rounded-md border">
      <table className={cn('w-full table-fixed text-xs', mono && 'font-mono')}>
        <tbody>
          {withIds(buildRows(parts)).map(({ row, id }) =>
            'gap' in row ? (
              <tr key={id}>
                <td
                  colSpan={2}
                  className="bg-neutral-50 px-2 py-1 text-center text-muted-foreground"
                >
                  {row.gap.toLocaleString()} unchanged lines
                </td>
              </tr>
            ) : (
              <tr key={id} className="border-b last:border-0">
                <Cell side={row.left} />
                <Cell side={row.right} />
              </tr>
            )
          )}
        </tbody>
      </table>
    </div>
  );
}
